import React, { useState } from 'react';
import '../styles/HealthDataSection.css';

interface HealthDataSectionProps {
  className?: string;
}

const HealthDataSection: React.FC<HealthDataSectionProps> = ({ className }) => {
  const [activeTab, setActiveTab] = useState('consultation');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const consultationRecords = [
    {
      id: 1,
      date: '2024-06-15',
      doctor: '张主任医师',
      department: '心血管内科',
      chief: '血脂异常复查',
      diagnosis: '高脂血症',
      symptoms: ['偶有心悸', '轻度胸闷'],
      advice: '继续服药，3个月后复查'
    },
    {
      id: 2,
      date: '2024-03-20',
      doctor: '李副主任医师',
      department: '呼吸内科',  
      chief: '肺结节随访',
      diagnosis: '肺部小结节',
      symptoms: ['无明显症状'],
      advice: '6个月后复查胸部CT'
    }
  ];

  const healthHistory = [
    {
      id: 1,
      type: '基本信息',
      date: '2024-06-15',
      condition: '个人档案更新',
      description: '身高175cm，体重70kg，血型A型RH阳性',
      status: 'normal'
    },
    {
      id: 2,
      type: 'BMI记录',
      date: '2024-06-15',
      condition: 'BMI 22.9',
      description: '体重指数正常范围，建议保持当前体重',
      status: 'normal'
    },
    {
      id: 3,
      type: 'BMI记录',
      date: '2024-01-15',
      condition: 'BMI 24.1',
      description: '体重指数略偏高，建议加强运动',
      status: 'caution'
    },
    {
      id: 4,
      type: '生活史',
      date: '2024-06-15',
      condition: '生活习惯评估',
      description: '不吸烟，偶尔饮酒，每周运动3次',
      status: 'normal'
    },
    {
      id: 5,
      type: '生活史',
      date: '2020-03-01',
      condition: '戒烟成功',
      description: '成功戒烟，之前吸烟史15年，每日1包',
      status: 'controlled'
    },
    {
      id: 6,
      type: '既往病史',
      date: '2018-05-12',
      condition: '高血压',
      description: '原发性高血压，血压控制良好',
      status: 'controlled'
    },
    {
      id: 7,
      type: '手术史',
      date: '2020-08-30',
      condition: '胆囊切除术',
      description: '腹腔镜胆囊切除术，术后恢复良好',
      status: 'completed'
    },
    {
      id: 8,
      type: '过敏史',
      date: '2019-03-15',
      condition: '青霉素过敏',
      description: '青霉素过敏反应，需避免使用',
      status: 'active'
    },
    {
      id: 9,
      type: '家族史',
      date: '遗传信息',
      condition: '糖尿病家族史',
      description: '父亲患2型糖尿病，需定期监测血糖',
      status: 'risk'
    }
  ];

  const medicalData = [
    {
      category: '血液检查',
      icon: '🩸',
      items: [
        { name: '血脂全套', date: '2024-06-15', fileType: 'PDF', imageUrl: '/images/blood-lipid-report.jpg', hospital: '市人民医院', status: 'abnormal' },
        { name: '血常规', date: '2024-06-15', fileType: '图片', imageUrl: '/images/blood-routine.jpg', hospital: '市人民医院', status: 'normal' },
        { name: '肝功能', date: '2024-06-15', fileType: 'PDF', imageUrl: '/images/liver-function.jpg', hospital: '市人民医院', status: 'caution' },
        { name: '肾功能', date: '2024-03-15', fileType: 'PDF', imageUrl: '/images/kidney-function.jpg', hospital: '市人民医院', status: 'normal' },
        { name: '血脂全套', date: '2024-01-10', fileType: 'PDF', imageUrl: '/images/blood-lipid-old.jpg', hospital: '市人民医院', status: 'caution' },
        { name: '血常规', date: '2023-12-20', fileType: '图片', imageUrl: '/images/blood-routine-old.jpg', hospital: '中心医院', status: 'normal' },
        { name: '甲状腺功能', date: '2023-08-15', fileType: 'PDF', imageUrl: '/images/thyroid.jpg', hospital: '内分泌医院', status: 'normal' }
      ]
    },
    {
      category: '影像检查',
      icon: '📸',
      items: [
        { name: '胸部CT', date: '2024-03-20', fileType: '图片', imageUrl: '/images/chest-ct.jpg', hospital: '中心医院', status: 'caution' },
        { name: '心脏彩超', date: '2024-06-15', fileType: '图片', imageUrl: '/images/heart-ultrasound.jpg', hospital: '市人民医院', status: 'normal' },
        { name: '腹部B超', date: '2024-06-15', fileType: '图片', imageUrl: '/images/abdomen-ultrasound.jpg', hospital: '市人民医院', status: 'caution' },
        { name: '胸部CT', date: '2023-09-10', fileType: '图片', imageUrl: '/images/chest-ct-old.jpg', hospital: '中心医院', status: 'normal' },
        { name: '腹部CT', date: '2023-05-20', fileType: '图片', imageUrl: '/images/abdomen-ct.jpg', hospital: '影像中心', status: 'normal' },
        { name: '颈部超声', date: '2023-02-15', fileType: '图片', imageUrl: '/images/neck-ultrasound.jpg', hospital: '市人民医院', status: 'normal' }
      ]
    },
    {
      category: '专科检查',
      icon: '⚕️',
      items: [
        { name: '心电图', date: '2024-06-15', fileType: '图片', imageUrl: '/images/ecg-report.jpg', hospital: '市人民医院', status: 'normal' },
        { name: '血压监测', date: '2024-06-15', fileType: 'PDF', imageUrl: '/images/blood-pressure.jpg', hospital: '市人民医院', status: 'caution' },
        { name: '血糖检测', date: '2024-06-15', fileType: '图片', imageUrl: '/images/blood-sugar.jpg', hospital: '市人民医院', status: 'normal' },
        { name: '24小时心电图', date: '2024-02-10', fileType: 'PDF', imageUrl: '/images/24h-ecg.jpg', hospital: '心血管医院', status: 'normal' },
        { name: '运动心电图', date: '2023-11-15', fileType: '图片', imageUrl: '/images/exercise-ecg.jpg', hospital: '体检中心', status: 'normal' }
      ]
    },
    {
      category: '病理检查',
      icon: '🔬',
      items: [
        { name: '胃镜检查', date: '2024-01-20', fileType: 'PDF', imageUrl: '/images/gastroscopy-report.jpg', hospital: '消化病医院', status: 'caution' },
        { name: '肠镜检查', date: '2023-07-15', fileType: 'PDF', imageUrl: '/images/colonoscopy.jpg', hospital: '消化病医院', status: 'normal' }
      ]
    }
  ];

  const medicationPlan = [
    {
      name: '阿托伐他汀钙片',
      specification: '20mg',
      dosage: '每晚一次，每次1片',
      duration: '长期服用',
      purpose: '降血脂',
      notes: '饭后服用，定期复查肝功能',
      status: 'active'
    },
    {
      name: '复合维生素B片',
      specification: '100片/瓶',
      dosage: '每日一次，每次2片',
      duration: '3个月',
      purpose: '营养补充',
      notes: '餐后服用',
      status: 'active'
    },
    {
      name: '奥美拉唑肠溶胶囊',
      specification: '20mg',
      dosage: '每日一次，每次1粒',
      duration: '4周',
      purpose: '护胃',
      notes: '空腹服用，停药需逐渐减量',
      status: 'completed'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return '#34c759';
      case 'caution': return '#ff9500';
      case 'abnormal': return '#ff3b30';
      case 'active': return '#007aff';
      case 'completed': return '#8e8e93';
      case 'controlled': return '#34c759';
      case 'risk': return '#ff9500';
      default: return '#8e8e93';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'normal': return '正常';
      case 'caution': return '注意';
      case 'abnormal': return '异常';
      case 'active': return '进行中';
      case 'completed': return '已完成';
      case 'controlled': return '已控制';
      case 'risk': return '风险';
      default: return '';
    }
  };

  // 获取类型对应的图标
  const getTypeIcon = (type: string) => {
    switch (type) {
      case '基本信息': return '👤';
      case 'BMI记录': return '📊';
      case '生活史': return '🏃';
      case '既往病史': return '📋';
      case '手术史': return '🏥';
      case '过敏史': return '⚠️';
      case '家族史': return '👨‍👩‍👧‍👦';
      default: return '📝';
    }
  };

  return (
    <div className={`health-data-section ${className || ''}`}>
      {/* 标签导航 */}
      <div className="health-data-tabs">
        <button 
          className={`health-tab ${activeTab === 'consultation' ? 'active' : ''}`}
          onClick={() => setActiveTab('consultation')}
        >
          问诊记录
        </button>
        <button 
          className={`health-tab ${activeTab === 'medication' ? 'active' : ''}`}
          onClick={() => setActiveTab('medication')}
        >
          用药计划
        </button>
        <button 
          className={`health-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          健康史
        </button>
        <button 
          className={`health-tab ${activeTab === 'medical' ? 'active' : ''}`}
          onClick={() => setActiveTab('medical')}
        >
          就诊资料
        </button>
      </div>

      {/* 问诊记录 */}
      {activeTab === 'consultation' && (
        <div className="consultation-records">
          {consultationRecords.map((record) => (
            <div key={record.id} className="consultation-card">
              <div className="consultation-header">
                <div className="consultation-date">{record.date}</div>
                <div className="consultation-doctor">
                  {record.doctor}
                </div>
              </div>
              
              <div className="consultation-content">
                <div className="consultation-summary">
                  <div className="diagnosis-text">{record.diagnosis}</div>
                  <div className="advice-brief">{record.advice}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 健康史 */}
      {activeTab === 'history' && (
                <div className="health-history-categories">
          {/* 按类型分组显示 */}
          {Object.entries(
            healthHistory.reduce((groups, item) => {
              if (!groups[item.type]) {
                groups[item.type] = [];
              }
              groups[item.type].push(item);
              return groups;
            }, {} as Record<string, typeof healthHistory>)
          ).map(([type, items]) => (
            <div key={type} className="history-category">
              <div className="history-category-header">
                <div className="category-title">
                  <span className="category-icon-large">{getTypeIcon(type)}</span>
                  <span className="category-name-large">{type}</span>
                </div>
                <div className="category-count-badge">{items.length}</div>
              </div>
              
              <div className="history-items">
                {items.map((item) => (
                  <div key={item.id} className="history-item-card">
                    <div className="history-item-header">
                      <div className="history-item-title">{item.condition}</div>
                      <div className="history-item-date">{item.date}</div>
                    </div>
                    <div className="history-item-description">{item.description}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 就诊资料 */}
      {activeTab === 'medical' && (
        <div className="medical-records-container">
          <div className="medical-records">
          {medicalData.map((category) => {
            const isExpanded = expandedCategories[category.category];
            const displayItems = isExpanded ? category.items : category.items.slice(0, 3);
            const hasMore = category.items.length > 3;
            
            return (
              <div key={category.category} className="medical-category">
                <div className="medical-category-header">
                  <h4 className="medical-category-title">
                    <span className="category-icon">{category.icon}</span>
                    <span className="category-name">{category.category}</span>
                    <span className="category-count">({category.items.length})</span>
                  </h4>
                  {hasMore && (
                    <button 
                      className="category-toggle-btn"
                      onClick={() => toggleCategory(category.category)}
                    >
                      {isExpanded ? '收起' : '更多'}
                      <span className="toggle-icon">{isExpanded ? '↑' : '↓'}</span>
                    </button>
                  )}
                </div>
                <div className="medical-items">
                  {displayItems.map((item, index) => (
                    <div key={index} className="medical-item">
                      <div className="medical-item-content">
                        <div className="medical-thumbnail">
                          <img 
                            src={item.imageUrl} 
                            alt={item.name} 
                            className="report-image"
                            onError={(e) => {
                              // 如果图片加载失败，显示占位符
                              e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiByeD0iOCIgZmlsbD0iI0Y4RjlGQSIgc3Ryb2tlPSIjRTVFNUVBIi8+CjxwYXRoIGQ9Ik0yNSAxNUM0NC44IDI1IDI1IDQ0LjggMTUgMjVTMjUgNS4yIDI1IDE1WiIgZmlsbD0iIzhFOEU5MyIvPgo8cGF0aCBkPSJNMTggMjBIMzJWMzBIMThaIiBmaWxsPSIjOEU4RTkzIi8+CjxyZWN0IHg9IjIxIiB5PSIyMiIgd2lkdGg9IjgiIGhlaWdodD0iNiIgZmlsbD0iI0Y4RjlGQSIvPgo8L3N2Zz4=';
                            }}
                          />
                          <div className="file-type-badge">{item.fileType}</div>
                        </div>
                        <div className="medical-details">
                          <div className="medical-info-row">
                            <span className="medical-name">{item.name}</span>
                            <span 
                              className="medical-status-badge"
                              style={{ backgroundColor: `${getStatusColor(item.status)}15`, color: getStatusColor(item.status) }}
                            >
                              {getStatusText(item.status)}
                            </span>
                          </div>
                          <div className="medical-meta">
                            <span className="medical-date">📅 {item.date}</span>
                            <span className="medical-hospital">🏥 {item.hospital}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          </div>
          
          {/* 底部添加按钮 */}
          <div className="bottom-add-section">
            <button className="bottom-add-btn">
              <span className="add-icon">+</span>
              添加检查资料
            </button>
            <p className="add-subtitle">系统将自动分类整理</p>
          </div>
        </div>
      )}

      {/* 用药计划 */}
      {activeTab === 'medication' && (
        <div className="ios-medication-section">
          {/* 快捷操作栏 */}
          <div className="ios-actions-bar">
            <div className="ios-action-item">
              <div className="ios-action-content">
                <div className="ios-action-title">录入药品</div>
                <div className="ios-action-subtitle">添加新的用药信息</div>
              </div>
              <div className="ios-action-arrow">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="#C7C7CC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            
            <div className="ios-action-item">
              <div className="ios-action-content">
                <div className="ios-action-title">一键续方</div>
                <div className="ios-action-subtitle">快速续开现有处方</div>
              </div>
              <div className="ios-action-arrow">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="#C7C7CC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          {/* 用药列表 */}
          <div className="ios-medication-list">
            {medicationPlan.map((medication, index) => (
              <div key={index} className="ios-medication-card">
                <div className="ios-med-header">
                  <div className="ios-med-main">
                    <div className="ios-med-title-row">
                      <span className="ios-med-name">{medication.name}</span>
                      <span className="ios-med-spec">{medication.specification}</span>
                    </div>
                  </div>
                  <div 
                    className="ios-med-badge"
                    style={{ 
                      backgroundColor: `${getStatusColor(medication.status)}15`, 
                      color: getStatusColor(medication.status) 
                    }}
                  >
                    {getStatusText(medication.status)}
                  </div>
                </div>
                
                <div className="ios-med-details">
                  <div className="ios-med-dosage">
                    <span className="ios-dosage-label">用法用量</span>
                    <span className="ios-dosage-text">{medication.dosage}</span>
                  </div>
                  
                  <div className="ios-med-duration">
                    <span className="ios-duration-label">疗程</span>
                    <span className="ios-duration-text">{medication.duration}</span>
                  </div>
                  
                  {medication.notes && (
                    <div className="ios-med-warning">
                      <span className="ios-warning-icon">⚠️</span>
                      <span className="ios-warning-text">{medication.notes}</span>
                                         </div>
                   )}
                 </div>
               </div>
            ))}
          </div>

          {/* 药品服务 */}
          <div className="medication-services">
            <div className="services-header">
              <h4>药品服务</h4>
              <p>专业药品推荐与比价服务</p>
            </div>
            
            <div className="service-cards">
              <div className="service-card">
                <div className="service-icon">🔍</div>
                <div className="service-content">
                  <h5>药品推荐</h5>
                  <p>根据病情智能推荐合适药品</p>
                </div>
                <div className="service-action">
                  <button className="service-btn">查看推荐</button>
                </div>
              </div>

              <div className="service-card">
                <div className="service-icon">⭐</div>
                <div className="service-content">
                  <h5>原研专区</h5>
                  <p>正品原研药品专业选购</p>
                </div>
                <div className="service-action">
                  <button className="service-btn">进入专区</button>
                </div>
              </div>

              <div className="service-card">
                <div className="service-icon">💰</div>
                <div className="service-content">
                  <h5>药品比价</h5>
                  <p>多平台价格对比，省钱购药</p>
                </div>
                <div className="service-action">
                  <button className="service-btn">比较价格</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthDataSection; 